import { MemoryStore } from "./memoryStore";
import { SupabaseStore } from "./supabaseStore";
import { persistDemo } from "./demoPersist";
import type { DataStore } from "./types";

export * from "./types";
export { MemoryStore, DEMO_TOTP_CODE } from "./memoryStore";

/**
 * Cloud connection comes from the build environment (VITE_SUPABASE_URL and
 * VITE_SUPABASE_PUBLISHABLE_KEY; the publishable key is public by design).
 * Without them the program runs in demo mode with in-memory data. Self sign up is
 * off unless VITE_ALLOW_SIGNUP=true (in production users are invited by the owner). VITE_DEMO_PERSIST=true
 * keeps the demo data in the browser's storage (end-to-end tests: reload, second tab).
 */
export function createStore(env: Record<string, string | undefined> = import.meta.env): DataStore {
  const url = env.VITE_SUPABASE_URL;
  const key = env.VITE_SUPABASE_PUBLISHABLE_KEY;
  if (url && key) return new SupabaseStore(url, key, { allowSignUp: env.VITE_ALLOW_SIGNUP === "true" });
  // the browser tests keep the demo data over reloads and tabs (VITE_DEMO_PERSIST=true at build time)
  // the browser tests switch the two-step sign in requirement on with a storage flag (DEMO_MFA_KEY)
  if (env.VITE_DEMO_PERSIST === "true") {
    const storage = browserStorage();
    return persistDemo(new MemoryStore({ ...DEMO_SUBSCRIPTION, mfa: readFlag(storage, DEMO_MFA_KEY) }), storage);
  }
  return new MemoryStore(DEMO_SUBSCRIPTION);
}

/** The demo starts like a new workshop in the cloud: Alap with the 30 day trial (Profi in its first week, then Alap). */
const DEMO_SUBSCRIPTION = { plan: "basic" as const, trialDays: 30 };

/** Browser tests only (kept demo): "on" makes the demo require two-step sign in like the cloud. */
export const DEMO_MFA_KEY = "garagevo.demoMfa";

function readFlag(storage: Storage | undefined, key: string): boolean {
  try {
    return storage?.getItem(key) === "on";
  } catch {
    return false;
  }
}

function browserStorage(): Storage | undefined {
  try {
    return globalThis.localStorage;
  } catch {
    return undefined;
  }
}

/** Reads what an email link brought the user here for, before the client consumes the URL. */
export function detectAuthIntent(hash: string, search = ""): import("./types").AuthIntent {
  const all = `${hash}&${search}`;
  if (/type=invite/.test(all)) return "invite";
  if (/type=recovery/.test(all)) return "recovery";
  return "none";
}
